import {
  Content as RadixContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronLeftSmallIcon } from '@dynatrace/strato-icons';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { MobileMenuNavigationContext } from './contexts/MobileMenuNavigationContext.js';
import { getSubTriggerSlots } from './item-slots.js';
import { groupContainer as menuGroupContainer } from './Menu.sty.js';
import * as styles from './MobileMenu.sty.js';
import { MobileMenuItem } from './MobileMenuItem.js';
import { type MenuContentProps } from './types.js';

type InternalMobileContentProps = MenuContentProps & {
  _sideOffset?: number;
  _alignOffset?: number;
  _contentId?: string;
};

interface ExitingPanel {
  level: ReturnType<typeof getSubTriggerSlots> | null;
  content: ReactNode;
  direction: 'forward' | 'back';
}

const SIDE_OFFSET = 4;

/**
 * @internal
 */
export const MobileContent = (props: InternalMobileContentProps) => {
  const {
    children,
    side = 'bottom',
    alignment = 'start',
    onCloseAutoFocus,
    onInteractOutside,
    avoidCollisions = true,
    collisionBoundary,
    _sideOffset = SIDE_OFFSET,
    _alignOffset: _unusedAlignOffset,
    _contentId,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props as InternalMobileContentProps;

  const intl = useIntl();
  const { stack, direction, pop } = useContext(MobileMenuNavigationContext);
  const { style: overlayStyle } = useOverlayStacking(undefined, 'overlay');
  const { focusProps: backFocusProps, focusClassName: backFocusClassName } =
    useFocusRing();

  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const previousStackDepth = useRef(stack.length);
  const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined);
  const [lockedSide, setLockedSide] = useState<typeof side | undefined>(
    undefined,
  );

  useLayoutEffect(() => {
    if (!panelRef.current) {
      return;
    }
    if (panelWidth === undefined) {
      setPanelWidth(panelRef.current.offsetWidth);
    }
    // Lock the side Radix chose on first open so navigation height changes
    // don't cause the menu to flip between above and below the trigger.
    if (lockedSide === undefined) {
      const dataSide = panelRef.current.getAttribute('data-side') as
        | typeof side
        | null;
      if (dataSide) {
        setLockedSide(dataSide);
      }
    }
  }, [panelWidth, lockedSide]);

  useLayoutEffect(() => {
    if (previousStackDepth.current === stack.length) {
      return;
    }
    previousStackDepth.current = stack.length;
    // Changing level unmounts the focused item. Radix restores focus to the
    // first item of the panel, which is the back button - focus belongs on the
    // first item of the level that was just entered or returned to.
    bodyRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
      ?.focus();
  }, [stack.length]);

  const currentLevel = stack.length > 0 ? stack[stack.length - 1] : null;
  const currentContent: ReactNode = currentLevel
    ? currentLevel.content
    : children;
  const titleSlots = currentLevel
    ? getSubTriggerSlots(currentLevel.title)
    : null;

  // Track previous panel so it can animate out while the new one animates in
  const [exiting, setExiting] = useState<ExitingPanel | null>(null);
  const prevStackLengthRef = useRef(stack.length);
  const prevContentRef = useRef<ReactNode>(currentContent);
  const prevTitleSlotsRef = useRef(titleSlots);

  useEffect(() => {
    if (stack.length !== prevStackLengthRef.current) {
      setExiting({
        level: prevTitleSlotsRef.current,
        content: prevContentRef.current,
        direction,
      });
      prevStackLengthRef.current = stack.length;
    }
    prevContentRef.current = currentContent;
    prevTitleSlotsRef.current = titleSlots;
  });

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (stack.length > 0) {
        pop();
        e.preventDefault();
      }
      e.stopPropagation();
    },
    [pop, stack.length],
  );

  const renderPanel = (
    level: ExitingPanel['level'],
    content: ReactNode,
    hasBackButton: boolean,
    contentRef?: RefObject<HTMLDivElement | null>,
  ) => (
    <>
      {hasBackButton && level && (
        <>
          <div className={styles.header}>
            <MobileMenuItem
              aria-label={intl.formatMessage({
                id: 'tSs2tyafv8WOwku3',
                defaultMessage: 'Back',
              })}
            >
              <button
                className={clsx(styles.backButton, backFocusClassName)}
                onClick={pop}
                type="button"
                role="menuitem"
                {...backFocusProps}
              >
                <ChevronLeftSmallIcon />
                <div className={styles.headerTitleWrapper}>
                  {level.prefix}
                  <span
                    className={clsx(
                      textStyleCSS({ textStyle: 'base-emphasized' }),
                      styles.headerTitleText,
                    )}
                  >
                    {level.freeform}
                  </span>
                </div>
              </button>
            </MobileMenuItem>
          </div>
          <div className={styles.headerDivider} />
        </>
      )}
      <div ref={contentRef} className={clsx(styles.body, menuGroupContainer)}>
        {content}
      </div>
    </>
  );

  return (
    <RadixPortal>
      <RadixContent
        {...remainingProps}
        ref={panelRef}
        id={_contentId}
        side={lockedSide ?? side}
        align={alignment}
        sideOffset={_sideOffset}
        avoidCollisions={avoidCollisions}
        collisionBoundary={collisionBoundary}
        onCloseAutoFocus={onCloseAutoFocus}
        onInteractOutside={onInteractOutside}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={styles.panel}
        style={{ zIndex: overlayStyle.zIndex, width: panelWidth }}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div className={styles.animationWrapper}>
          {exiting && (
            <div
              aria-hidden="true"
              className={
                exiting.direction === 'forward'
                  ? styles.exitForward
                  : styles.exitBack
              }
            >
              {renderPanel(
                exiting.level,
                exiting.content,
                exiting.level !== null,
              )}
            </div>
          )}
          <div
            key={stack.length}
            className={clsx(
              styles.panelContent,
              exiting &&
                (direction === 'forward'
                  ? styles.enterForward
                  : styles.enterBack),
            )}
            onAnimationEnd={exiting ? () => setExiting(null) : undefined}
          >
            {renderPanel(
              titleSlots,
              currentContent,
              currentLevel !== null,
              bodyRef,
            )}
          </div>
        </div>
      </RadixContent>
    </RadixPortal>
  );
};

(MobileContent as typeof MobileContent & { displayName: string }).displayName =
  'Menu.Content';
